import React, { useEffect, useState } from 'react';
import { FaNewspaper, FaUpload, FaPaperPlane, FaChartBar } from 'react-icons/fa';
import Header from '../Header';
import api from '../services/apiService';
import '../scss/admin/ProductNewsManager.scss';

const AUDIENCE_OPTIONS = [
  { key: 'parents', label: 'Parents' },
  { key: 'coaches', label: 'Coaches' },
  { key: 'managers', label: 'Managers' },
  { key: 'club_secretaries', label: 'Club Secretaries' },
  { key: 'players', label: 'Players' },
];

const emptyForm = {
  title: '',
  description: '',
  image_url: '',
  button_label: '',
  button_url: '',
  audiences: ['coaches'],
  academy_ids: [],
  team_ids: [],
  send_push: true,
  test_to_self: true,
};

const teamLabel = (team) => {
  const name = String(team.team_name || `Team ${team.team_id}`).trim();
  const age = String(team.age_group || '').trim();
  if (age && !name.toLowerCase().includes(age.toLowerCase())) {
    return `${name} · ${age}`;
  }
  return name;
};

const ProductNewsManager = () => {
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clickStats, setClickStats] = useState(null);
  const [clickLoadingId, setClickLoadingId] = useState(null);
  const [academies, setAcademies] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamSearch, setTeamSearch] = useState('');

  useEffect(() => {
    fetchNews();
    fetchTargets();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/admin/list_product_news.php`);
      if (response.data.success) {
        setNewsList(response.data.news || []);
      } else {
        setError(response.data.message || 'Could not load product news');
      }
    } catch (err) {
      console.error(err);
      setError('Could not load product news. Has the migration been run?');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const fetchTargets = async () => {
    try {
      const [academyResponse, teamResponse] = await Promise.all([
        api.get(`${process.env.REACT_APP_API_URL}/admin/academy/get_academy_subscriptions.php`),
        api.get(`${process.env.REACT_APP_API_URL}/get_teams_directory.php`),
      ]);
      setAcademies(academyResponse.data?.success ? (academyResponse.data.academies || []) : []);
      setTeams(teamResponse.data?.success ? (teamResponse.data.teams || []) : []);
    } catch (err) {
      console.error(err);
    }
  };

  const selectedAcademyTeamIds = new Set(
    academies
      .filter((academy) => form.academy_ids.includes(Number(academy.id)))
      .flatMap((academy) => (academy.teams || []).map((team) => Number(team.team_id)))
      .filter((id) => id > 0)
  );

  const selectableTeams = (() => {
    const byId = new Map();
    teams.forEach((team) => {
      const id = Number(team.team_id);
      if (id) {
        byId.set(id, team);
      }
    });
    academies.forEach((academy) => {
      (academy.teams || []).forEach((team) => {
        const id = Number(team.team_id);
        if (id && !byId.has(id)) {
          byId.set(id, team);
        }
      });
    });
    return Array.from(byId.values()).sort((a, b) => teamLabel(a).localeCompare(teamLabel(b), 'en', {
      sensitivity: 'base',
      numeric: true,
    }));
  })();

  const visibleTeams = selectableTeams.filter((team) => {
    const query = teamSearch.trim().toLowerCase();
    if (!query) {
      return true;
    }
    return teamLabel(team).toLowerCase().includes(query);
  });

  const targetSummary = (() => {
    const academyNames = academies
      .filter((academy) => form.academy_ids.includes(Number(academy.id)))
      .map((academy) => academy.name || `Academy ${academy.id}`);
    const extraTeams = selectableTeams
      .filter((team) => form.team_ids.includes(Number(team.team_id)) && !selectedAcademyTeamIds.has(Number(team.team_id)))
      .map((team) => teamLabel(team));
    const labels = [...academyNames, ...extraTeams];
    if (labels.length === 0) {
      return 'All teams';
    }
    const teamCount = new Set([
      ...selectedAcademyTeamIds,
      ...form.team_ids.map(Number),
    ]).size;
    return `${labels.join(', ')} (${teamCount} ${teamCount === 1 ? 'team' : 'teams'})`;
  })();

  const toggleAudience = (key) => {
    setForm((prev) => {
      const exists = prev.audiences.includes(key);
      return {
        ...prev,
        audiences: exists
          ? prev.audiences.filter((a) => a !== key)
          : [...prev.audiences, key],
      };
    });
  };

  const toggleAcademy = (academyId) => {
    setForm((prev) => {
      const exists = prev.academy_ids.includes(academyId);
      return {
        ...prev,
        academy_ids: exists
          ? prev.academy_ids.filter((id) => id !== academyId)
          : [...prev.academy_ids, academyId],
      };
    });
  };

  const toggleTeam = (teamId) => {
    if (selectedAcademyTeamIds.has(teamId)) {
      return;
    }
    setForm((prev) => {
      const exists = prev.team_ids.includes(teamId);
      return {
        ...prev,
        team_ids: exists
          ? prev.team_ids.filter((id) => id !== teamId)
          : [...prev.team_ids, teamId],
      };
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, image_url: '' }));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
    setForm((prev) => ({ ...prev, image_url: '' }));
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) {
      return form.image_url || '';
    }

    setUploading(true);
    try {
      const payload = new FormData();
      payload.append('image', imageFile);
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/admin/upload_product_news_image.php`,
        payload
      );
      if (!response.data.success || !response.data.url) {
        throw new Error(response.data.message || 'Image upload failed');
      }
      return response.data.url;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required');
      return;
    }
    if (form.audiences.length === 0) {
      setError('Choose at least one audience');
      return;
    }
    if (form.button_url.trim() && !/^https?:\/\//i.test(form.button_url.trim())) {
      setError('Button URL must start with http:// or https://');
      return;
    }

    setSubmitting(true);
    try {
      const imageUrl = await uploadImageIfNeeded();
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/admin/create_product_news.php`,
        {
          title: form.title.trim(),
          description: form.description.trim(),
          image_url: imageUrl,
          button_label: form.button_label.trim(),
          button_url: form.button_url.trim(),
          audiences: form.audiences,
          academy_ids: form.academy_ids,
          team_ids: form.team_ids.filter((id) => !selectedAcademyTeamIds.has(id)),
          send_push: form.send_push,
          test_to_self: form.test_to_self,
        },
        { timeout: 20000 }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to publish');
      }

      let pushSummary = ' Saved to News feed only (no push).';
      if (form.send_push) {
        const scope = form.test_to_self ? ' (you only)' : '';
        pushSummary = ` Push${scope} is sending in the background — refresh the list for delivery counts.`;
      }
      if (form.test_to_self) {
        pushSummary += ' Test item is hidden from other users’ News feeds.';
      }

      setSuccess(`Published successfully.${pushSummary}`);
      setForm(emptyForm);
      setTeamSearch('');
      setImageFile(null);
      setImagePreview('');
      await fetchNews();
      setTimeout(fetchNews, 4000);
    } catch (err) {
      console.error(err);
      const timedOut = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '');
      setError(
        timedOut
          ? 'Request timed out while publishing. If you still received the push, the item may have saved — refresh the list.'
          : err.message || 'Failed to publish product news'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatAudiences = (audiences) => {
    if (!Array.isArray(audiences) || audiences.length === 0) return '—';
    return audiences
      .map((key) => AUDIENCE_OPTIONS.find((o) => o.key === key)?.label || key)
      .join(', ');
  };

  const loadClicks = async (newsId) => {
    setClickLoadingId(newsId);
    setClickStats(null);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/admin/get_product_news_clicks.php?news_id=${newsId}`
      );
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not load clicks');
      }
      setClickStats({ newsId, ...response.data });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not load click stats');
    } finally {
      setClickLoadingId(null);
    }
  };

  return (
    <div className="product-news-manager container">
      <Header />
      <h1>
        <FaNewspaper /> Product News
      </h1>
      <p className="intro">
        Publish a news item to the in-app News feed and send a push notification
        (push only — no SMS). Image and button are optional.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form className="news-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="news-title">Title</label>
          <input
            id="news-title"
            name="title"
            type="text"
            value={form.title}
            onChange={handleInputChange}
            maxLength={255}
            required
            placeholder="What's new"
          />
        </div>

        <div className="form-group">
          <label htmlFor="news-image">Image <span className="optional">(optional)</span></label>
          <div className="image-upload">
            {(imagePreview || form.image_url) && (
              <img
                src={imagePreview || form.image_url}
                alt="News preview"
                className="image-preview"
              />
            )}
            <div className="image-actions">
              <label className="upload-button">
                <FaUpload /> {imageFile || form.image_url ? 'Change image' : 'Upload image'}
                <input
                  id="news-image"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageSelect}
                  hidden
                />
              </label>
              {(imageFile || form.image_url || imagePreview) && (
                <button type="button" className="clear-image" onClick={clearImage}>
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="news-description">Description</label>
          <textarea
            id="news-description"
            name="description"
            value={form.description}
            onChange={handleInputChange}
            rows={5}
            required
            placeholder="Short summary for the News feed and push notification body"
          />
        </div>

        <fieldset className="form-group audience-fieldset">
          <legend>Button <span className="optional">(optional)</span></legend>
          <p className="hint">Leave blank for no CTA. URL is required only if you want a button.</p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="news-button-label">Button label</label>
              <input
                id="news-button-label"
                name="button_label"
                type="text"
                value={form.button_label}
                onChange={handleInputChange}
                maxLength={100}
                placeholder="Learn more"
              />
            </div>
            <div className="form-group">
              <label htmlFor="news-button-url">Button URL</label>
              <input
                id="news-button-url"
                name="button_url"
                type="text"
                inputMode="url"
                value={form.button_url}
                onChange={handleInputChange}
                placeholder="https://…"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="form-group audience-fieldset">
          <legend>Audience</legend>
          <p className="hint">Who should see this in News and receive the push?</p>
          <div className="audience-grid">
            {AUDIENCE_OPTIONS.map((option) => (
              <label key={option.key} className="audience-option">
                <input
                  type="checkbox"
                  checked={form.audiences.includes(option.key)}
                  onChange={() => toggleAudience(option.key)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="form-group audience-fieldset">
          <legend>Teams</legend>
          <p className="hint">
            Leave this blank to send to every team. Choose an academy to include every team assigned to it, or pick individual teams.
          </p>
          <p className="hint target-summary">{targetSummary}</p>
          {academies.length > 0 && (
            <div className="audience-grid">
              {academies.map((academy) => {
                const academyId = Number(academy.id);
                const count = (academy.teams || []).length;
                return (
                  <label key={academyId} className="audience-option">
                    <input
                      type="checkbox"
                      checked={form.academy_ids.includes(academyId)}
                      onChange={() => toggleAcademy(academyId)}
                    />
                    <span>
                      {academy.name || `Academy ${academyId}`}
                      <small>{count === 1 ? '1 team' : `${count} teams`}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <label className="team-search-label" htmlFor="news-team-search">Individual teams</label>
          <input
            id="news-team-search"
            type="text"
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
            placeholder="Search teams"
          />
          <div className="target-list">
            {visibleTeams.length === 0 ? (
              <p className="hint">No teams match that search.</p>
            ) : visibleTeams.map((team) => {
              const teamId = Number(team.team_id);
              const viaAcademy = selectedAcademyTeamIds.has(teamId);
              const academyNames = academies
                .filter((academy) => form.academy_ids.includes(Number(academy.id))
                  && (academy.teams || []).some((row) => Number(row.team_id) === teamId))
                .map((academy) => academy.name || 'Academy');
              return (
                <label key={teamId} className="audience-option">
                  <input
                    type="checkbox"
                    checked={viaAcademy || form.team_ids.includes(teamId)}
                    disabled={viaAcademy}
                    onChange={() => toggleTeam(teamId)}
                  />
                  <span>
                    {teamLabel(team)}
                    {viaAcademy && (
                      <small>Included with {academyNames.join(', ')}</small>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <label className="send-push-option">
          <input
            type="checkbox"
            name="test_to_self"
            checked={form.test_to_self}
            onChange={handleInputChange}
          />
          <span>Test mode — push to me only (hidden from other users’ News)</span>
        </label>

        <label className="send-push-option">
          <input
            type="checkbox"
            name="send_push"
            checked={form.send_push}
            onChange={handleInputChange}
          />
          <span>Send push notification now</span>
        </label>

        <button
          type="submit"
          className="publish-button"
          disabled={submitting || uploading}
        >
          <FaPaperPlane />
          {submitting || uploading
            ? 'Publishing…'
            : form.test_to_self
              ? 'Publish test (me only)'
              : 'Publish news'}
        </button>
      </form>

      <section className="news-history">
        <h2>Recently published</h2>
        {loading ? (
          <p>Loading…</p>
        ) : newsList.length === 0 ? (
          <p className="empty">No product news yet.</p>
        ) : (
          <ul className="news-list">
            {newsList.map((item) => (
              <li key={item.news_id} className="news-list-item">
                {item.image_url && (
                  <img src={item.image_url} alt="" className="thumb" />
                )}
                <div className="news-meta">
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                  <span className="meta-line">
                    {formatAudiences(item.audiences)}
                    {` · ${item.target_summary || 'All teams'}`}
                    {item.created_at ? ` · ${new Date(item.created_at).toLocaleString('en-GB')}` : ''}
                  </span>
                  <span className="meta-line">
                    {item.push_sent
                      ? `Push: ${item.push_success_count || 0} ok / ${item.push_failure_count || 0} failed`
                      : 'Push pending or skipped'}
                    {` · Clicks: ${item.click_count || 0}`}
                    {item.button_url ? ` · CTA: ${item.button_label || 'Open'}` : ''}
                  </span>
                  <button
                    type="button"
                    className="clicks-button"
                    onClick={() => loadClicks(item.news_id)}
                    disabled={clickLoadingId === item.news_id}
                  >
                    <FaChartBar />
                    {clickLoadingId === item.news_id ? 'Loading…' : 'Who clicked'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {clickStats && (
        <section className="click-stats">
          <h2>Clicks for news #{clickStats.newsId}</h2>
          <p className="meta-line">
            Total {clickStats.stats?.total_clicks || 0}
            {' · '}Unique {clickStats.stats?.unique_actors || 0}
            {' · '}Feed {clickStats.stats?.feed_clicks || 0}
            {' · '}CTA {clickStats.stats?.cta_clicks || 0}
          </p>
          {(clickStats.clicks || []).length === 0 ? (
            <p className="empty">No clicks recorded yet.</p>
          ) : (
            <ul className="click-list">
              {clickStats.clicks.map((click) => (
                <li key={click.click_id}>
                  <strong>{click.user_type || 'user'} #{click.user_id || '—'}</strong>
                  {' · '}{click.source}
                  {click.phone ? ` · ${click.phone}` : ''}
                  {' · '}
                  {click.created_at
                    ? new Date(click.created_at).toLocaleString('en-GB')
                    : ''}
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="clear-image" onClick={() => setClickStats(null)}>
            Close
          </button>
        </section>
      )}
    </div>
  );
};

export default ProductNewsManager;

